/**
 * CompoundDetailPopover.jsx — iter913 (Feb 2026)
 *
 * GDPR-safe compound reference. Opens as a right-side drawer when the
 * user clicks any compound name in their Personal Plan. Shows:
 *   – PubChem / HMDB / KEGG / ChEBI / PhenolExplorer links
 *   – Molecular properties (formula, weight, xLogP, TPSA)
 *   – Bulgarian synonyms
 *   – CTD-linked KEGG pathways impacted
 *   – Prominent "Обсъди с лекар" CTA
 *   – Educational disclaimer
 *
 * This screen is deliberately READ-ONLY reference material. It does NOT
 * suggest doses, does NOT imply a prescription, and reminds the user
 * three times (banner, pill on the header, footer CTA) that a doctor
 * consultation is required.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  X, ExternalLink, Loader2, AlertCircle, Stethoscope, Beaker, Link2, Info, Send,
} from "lucide-react";
import { useAuth } from "@/App";

const LINK_LABELS = {
  pubchem:         "PubChem",
  hmdb:            "HMDB",
  kegg:            "KEGG",
  chebi:           "ChEBI",
  phenol_explorer: "PhenolExplorer",
};

const PROP_LABELS_BG = {
  molecular_formula: "Формула",
  molecular_weight:  "Мол. тегло",
  iupac_name:        "IUPAC",
  inchikey:          "InChIKey",
  smiles:            "SMILES",
  xlogp:             "LogP",
  tpsa:              "TPSA",
  h_bond_donors:     "H-донори",
  h_bond_acceptors:  "H-акцептори",
  charge:            "Заряд",
  complexity:        "Комплексност",
};

export default function CompoundDetailPopover({
  open,
  query,
  onClose,
  isDark = false,
  testid = "compound-detail-popover",
}) {
  const { API } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData]       = useState(null);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!open || !query) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    axios.get(`${API}/compound-detail`, { params: { q: query, type: "name" } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e?.response?.data?.detail || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, query, API]);

  if (!open) return null;

  const panelCls = isDark
    ? "bg-slate-900 text-slate-100 border-l border-slate-700"
    : "bg-white text-slate-800 border-l border-slate-200";

  return (
    <div
      data-testid={testid}
      className="fixed inset-0 z-[100] flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      aria-labelledby="compound-detail-title"
    >
      {/* Backdrop */}
      <button
        aria-label="Close backdrop"
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        data-testid={`${testid}-backdrop`}
      />

      {/* Right-side drawer */}
      <aside
        className={`relative w-full sm:max-w-md md:max-w-lg h-full shadow-2xl overflow-y-auto ${panelCls}`}
      >
        {/* Sticky header with prominent doctor CTA */}
        <div className={`sticky top-0 z-10 ${isDark ? "bg-slate-900" : "bg-white"} pb-2`}>
          <div className={`px-4 pt-4 pb-2 border-b ${isDark ? "border-slate-700" : "border-slate-200"} flex items-start gap-3`}>
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0
              ${isDark ? "bg-violet-500/20 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
              <Beaker className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div
                id="compound-detail-title"
                className="text-sm font-bold truncate"
                data-testid={`${testid}-title`}
              >
                {data?.display_name || query}
              </div>
              <div className={`text-[10.5px] mt-0.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                Референтна информация · не е медицински съвет
              </div>
            </div>
            <button
              onClick={onClose}
              aria-label="Затвори"
              data-testid={`${testid}-close`}
              className={`p-1.5 rounded-lg ${isDark ? "hover:bg-slate-800" : "hover:bg-slate-100"}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* GDPR / consult-doctor persistent banner */}
          <div
            className={`mx-4 mt-2 rounded-lg p-2.5 text-[11px] flex items-start gap-2
              ${isDark ? "bg-amber-500/15 text-amber-100 border border-amber-500/30"
                       : "bg-amber-50 text-amber-900 border border-amber-200"}`}
            data-testid={`${testid}-doctor-banner`}
          >
            <Stethoscope className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              <b>Задължителна консултация с лекар</b> преди прием. Дозирането,
              лекарствените взаимодействия и противопоказанията се определят
              индивидуално от лекуващия лекар.
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 pt-3 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-xs opacity-70">
              <Loader2 className="w-4 h-4 animate-spin" />
              Зареждам от PubChem / HMDB / CTD…
            </div>
          )}
          {error && (
            <div className={`rounded-lg p-3 text-xs ${isDark ? "bg-rose-500/15 text-rose-200" : "bg-rose-50 text-rose-700"}`}>
              <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
              Error: {error}
            </div>
          )}

          {data && data.found === false && (
            <div className={`rounded-lg p-3 text-xs ${isDark ? "bg-slate-800" : "bg-slate-50"}`}>
              <Info className="w-3.5 h-3.5 inline mr-1" />
              Няма данни в локалните източници. {data.message}
            </div>
          )}

          {data && data.found !== false && (
            <>
              {/* Class chip row */}
              {(data.pe_class || data.pe_subclass || data.cas_rn) && (
                <div className="flex flex-wrap gap-1.5 text-[10.5px]" data-testid={`${testid}-tags`}>
                  {data.pe_class && (
                    <span className={`px-2 py-0.5 rounded-full font-semibold
                      ${isDark ? "bg-fuchsia-500/25 text-fuchsia-200" : "bg-fuchsia-100 text-fuchsia-800"}`}>
                      {data.pe_class}
                    </span>
                  )}
                  {data.pe_subclass && (
                    <span className={`px-2 py-0.5 rounded-full
                      ${isDark ? "bg-slate-700 text-slate-200" : "bg-slate-100 text-slate-700"}`}>
                      {data.pe_subclass}
                    </span>
                  )}
                  {data.cas_rn && (
                    <span className={`px-2 py-0.5 rounded-full font-mono text-[10px]
                      ${isDark ? "bg-slate-800 text-slate-300" : "bg-slate-50 text-slate-600 border border-slate-200"}`}>
                      CAS {data.cas_rn}
                    </span>
                  )}
                </div>
              )}

              {/* External links */}
              {data.links && Object.values(data.links).some(Boolean) && (
                <section data-testid={`${testid}-links`}>
                  <div className={`text-[10.5px] uppercase tracking-wider mb-1.5 flex items-center gap-1 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    <Link2 className="w-3 h-3" /> Референции
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    {Object.entries(data.links).map(([key, url]) =>
                      url ? (
                        <a
                          key={key}
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`${testid}-link-${key}`}
                          className={`inline-flex items-center justify-between text-[11px] rounded-lg px-2.5 py-1.5 transition
                            ${isDark ? "bg-slate-800 hover:bg-slate-700 text-slate-100" : "bg-slate-50 hover:bg-slate-100 text-slate-800 border border-slate-200"}`}
                        >
                          <span className="font-semibold">{LINK_LABELS[key]}</span>
                          <ExternalLink className="w-3 h-3 opacity-60" />
                        </a>
                      ) : null,
                    )}
                  </div>
                </section>
              )}

              {/* Molecular properties */}
              {data.properties && Object.keys(data.properties).length > 0 && (
                <section data-testid={`${testid}-properties`}>
                  <div className={`text-[10.5px] uppercase tracking-wider mb-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Молекулни свойства
                  </div>
                  <div className={`rounded-lg text-[11px] divide-y ${isDark ? "bg-slate-800/60 divide-slate-700" : "bg-slate-50 divide-slate-200"}`}>
                    {Object.entries(data.properties).map(([k, v]) => (
                      v !== null && v !== undefined && v !== "" ? (
                        <div key={k} className="flex items-center justify-between px-2.5 py-1 gap-3">
                          <span className={isDark ? "text-slate-400" : "text-slate-500"}>
                            {PROP_LABELS_BG[k] || k}
                          </span>
                          <span className="font-mono text-[10.5px] text-right break-all max-w-[70%] opacity-90">
                            {String(v)}
                          </span>
                        </div>
                      ) : null
                    ))}
                  </div>
                </section>
              )}

              {/* Pathway impact */}
              {data.pathway_impact_count > 0 && (
                <section data-testid={`${testid}-pathways`}>
                  <div className={`text-[10.5px] uppercase tracking-wider mb-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    KEGG пътища с CTD литература ({data.pathway_impact_count})
                  </div>
                  <div className="space-y-1">
                    {data.pathway_impact.map((p) => (
                      <div key={p.pathway_id}
                        className={`rounded-lg px-2.5 py-1.5 text-[11px] flex items-center justify-between
                          ${isDark ? "bg-slate-800/60" : "bg-slate-50"}`}
                        data-testid={`${testid}-pathway-${p.pathway_id}`}
                      >
                        <span>
                          <b>{p.name_bg}</b>
                          <span className={`ml-1 text-[9.5px] font-mono ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                            {p.kegg}
                          </span>
                        </span>
                        {p.corrected_p_value !== null && p.corrected_p_value !== undefined && (
                          <span className={`text-[9.5px] font-mono ${isDark ? "text-emerald-300" : "text-emerald-700"}`}>
                            p={Number(p.corrected_p_value).toExponential(1)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Synonyms */}
              {(data.synonyms || []).length > 0 && (
                <section data-testid={`${testid}-synonyms`}>
                  <div className={`text-[10.5px] uppercase tracking-wider mb-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
                    Синоними
                  </div>
                  <div className={`text-[10.5px] rounded-lg p-2 ${isDark ? "bg-slate-800/50 text-slate-300" : "bg-slate-50 text-slate-600"}`}>
                    {data.synonyms.slice(0, 8).join(" · ")}
                    {data.synonyms.length > 8 && <span className="opacity-60"> · +{data.synonyms.length - 8}</span>}
                  </div>
                </section>
              )}

              {/* Sources footer */}
              {(data.sources || []).length > 0 && (
                <div className={`text-[10px] pt-2 border-t ${isDark ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
                  Източници: {data.sources.join(" · ")}
                </div>
              )}

              {/* iter914 — Send to doctor CTA */}
              <button
                onClick={() => {
                  // Backend expects auth via Bearer — session cookie may not travel to a new tab,
                  // so we fetch the HTML with axios then open a blob URL. This preserves auth.
                  axios.get(`${API}/compound-detail/doctor-summary`, {
                    params:   { q: query, type: "name" },
                    responseType: "blob",
                  }).then((r) => {
                    const blob = new Blob([r.data], { type: "text/html;charset=utf-8" });
                    const url  = URL.createObjectURL(blob);
                    window.open(url, "_blank", "noopener,noreferrer");
                    setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  }).catch(() => {
                    alert("Не успях да генерирам PDF доклада. Опитай отново.");
                  });
                }}
                data-testid={`${testid}-doctor-summary`}
                className={`w-full inline-flex items-center justify-center gap-2 rounded-xl py-2.5 text-xs font-bold transition
                  ${isDark
                    ? "bg-gradient-to-r from-sky-500 to-cyan-500 text-white hover:opacity-90"
                    : "bg-gradient-to-r from-sky-500 to-cyan-500 text-white hover:opacity-90"}`}
              >
                <Send className="w-3.5 h-3.5" />
                Изпрати на лекар · PDF доклад
              </button>

              {/* Bottom disclaimer (repeats banner intent) */}
              <div className={`rounded-lg p-3 text-[11px] font-medium
                ${isDark ? "bg-amber-500/15 text-amber-100 border border-amber-500/30"
                         : "bg-amber-50 text-amber-900 border border-amber-200"}`}
                data-testid={`${testid}-disclaimer`}
              >
                {data.disclaimer}
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
